import React from "react";
import {
  Composition,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

export const MyComposition = () => {
  return (
    <Composition
      id="MotionDemo"
      component={MotionGraphic}
      durationInFrames={120}
      fps={30}
      width={1080}
      height={1920}
    />
  );
};

export const MotionGraphic: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Spring animations
  const titleScale = spring({
    frame,
    fps,
    config: { damping: 12, mass: 0.5, stiffness: 100 },
  });

  const badgeSlide = spring({
    frame: frame - 15,
    fps,
    config: { damping: 14 },
  });

  const cardSlide = spring({
    frame: frame - 30,
    fps,
    config: { damping: 15 },
  });

  const glowOpacity = interpolate(
    Math.sin(frame / 10),
    [-1, 1],
    [0.4, 0.9]
  );

  return (
    <div
      style={{
        flex: 1,
        backgroundColor: "#0a0a0f",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        fontFamily: "system-ui, -apple-system, sans-serif",
        color: "#ffffff",
        overflow: "hidden",
      }}
    >
      {/* Background ambient glow */}
      <div
        style={{
          position: "absolute",
          width: 800,
          height: 800,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(99,102,241,0.25) 0%, rgba(168,85,247,0.1) 50%, transparent 70%)",
          opacity: glowOpacity,
          transform: `scale(${1 + Math.sin(frame / 20) * 0.1})`,
        }}
      />

      {/* Top Badge */}
      <div
        style={{
          opacity: badgeSlide,
          transform: `translateY(${(1 - badgeSlide) * -50}px)`,
          padding: "12px 28px",
          borderRadius: 999,
          background: "linear-gradient(90deg, #6366f1, #a855f7)",
          fontSize: 28,
          fontWeight: 700,
          letterSpacing: 2,
          textTransform: "uppercase",
          marginBottom: 40,
          boxShadow: "0 10px 30px rgba(99,102,241,0.4)",
        }}
      >
        ✦ Studio Konten AI ✦
      </div>

      {/* Kinetic Headline */}
      <h1
        style={{
          fontSize: 92,
          fontWeight: 900,
          textAlign: "center",
          lineHeight: 1.1,
          margin: "0 40px 30px",
          transform: `scale(${titleScale})`,
          background: "linear-gradient(180deg, #ffffff 40%, #94a3b8 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        REMOTION × CLAUDE
      </h1>

      {/* Subtitle / Feature Card */}
      <div
        style={{
          opacity: cardSlide,
          transform: `translateY(${(1 - cardSlide) * 60}px)`,
          background: "rgba(255, 255, 255, 0.05)",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          backdropFilter: "blur(20px)",
          borderRadius: 32,
          padding: "36px 54px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.5)",
        }}
      >
        <span style={{ fontSize: 36, color: "#38bdf8", fontWeight: 700 }}>
          Prompt to Motion Graphics
        </span>
        <span style={{ fontSize: 26, color: "#94a3b8", textAlign: "center", maxWidth: 600 }}>
          100% Code-Driven React Animations · 60fps Native Render
        </span>
      </div>
    </div>
  );
};
